import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { parseISO } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { importInvoices } from '~/api/documents';
import { fetchAllFlows } from '~/api/flow';
import { getEInvoice } from '~/api/invoicing';
import { backendApiGateway } from '~/client/backend';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useMapResponseErrors } from '~/hooks/document-builder/useMapResponseErrors';
import { useActiveCompany, useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger, { captureException } from '~/hooks/useExceptionLogger';
import { AnalyticsEvents } from '~/services/analytics';
import { invalidateDocumentsAndFlowsCache, useLastDocumentsNumberQuery } from '~/services/document';
import { useGrossPricesState } from '~/services/items';
import { CashflowDataDirection } from '~/types/cashflow';
import { Company, CompanyQueryKeys } from '~/types/company';
import { IdentityType } from '~/types/company-identity';
import { Counterpart } from '~/types/counterparts';
import {
  Document,
  DocumentDirection,
  DocumentFormat,
  DocumentQueryKeys,
  DocumentResourceType,
  DocumentStatus,
  DocumentType,
  DocumentWithAttachments,
  EInvoiceType,
  InvoiceImportPayload,
} from '~/types/document';
import {
  DocumentBuilderFlowForm,
  DocumentBuilderForm,
  DocumentBuilderLine,
  DocumentDataLine,
  PaymentMethodForm,
  RelatedDocumentLine,
  RelatedDocumentType,
} from '~/types/document-builder';
import { FlowPaymentStatus } from '~/types/flow';
import {
  DocumentUpdateRequest,
  EsigibilitaIva,
  InvoicesMutationKeys,
  InvoicesQueryKeys,
  ItalianEBill,
  ItalianEInvoice,
  SharedEInvoice,
} from '~/types/invoicing';
import { assertIsDefined } from '~/types/typeGuards';
import { defaultDestinationCode } from '~/utils/counterparts';
import { defaultDiscount, fillValues } from '~/utils/document-builder';
import {
  eInvoiceAnalytics,
  esigibilitaToVatCollection,
  parseDocumentVatCollection,
  parseEInvoiceItem,
  eInvoiceToForm,
  valuesToEInvoice,
} from '~/utils/einvoice';
import { calculatePresetDate, parseDocumentFlow } from '~/utils/flow';
import { formatDateForAPI } from '~/utils/formatDate';
import {
  defaultEInvoiceType,
  getHighestInvoiceNumber,
  highestInvoiceNumbersByPattern,
  increaseInvoiceNumber,
  datiPagamentoFromFlows,
  prepareCreateCreditNoteData,
  prepareCreateDeliveryNoteFromAsideData,
  prepareCreateInvoiceFromAsideData,
  highestInvoiceNumbersBySectional,
  DocumentNumberWithSectional,
} from '~/utils/invoicing';
import { adjustDocumentToUpsertByRules } from '~/utils/invoicing/globals';
import {
  buildJsonAPIQueryParams,
  jsonAPIExtraQueryBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
} from '~/utils/jsonapi-query';
import { documentTypeToRelatedDocument } from '~/utils/relatedDocument';
import { useInvoicingSettings } from './company-settings';
import { useUpdateCounterpart } from './counterparts';
import { useCreateFlow, useDeleteFlow, useUpdateFlow } from './flow';

const includeBuilder = jsonAPIIncludeBuilder([
  'attachments',
  'category',
  'company',
  'company.companyIdentity',
  'counterpart',
  'flows',
  'sectional',
  'subcategory',
  'related',
]);

const decorateEInvoiceWithFlows = async (documentId: string, eInvoice: ItalianEInvoice) => {
  const flows = await fetchAllFlows(documentId);

  assertIsDefined(eInvoice.fatturaElettronicaBody[0], 'body must be defined');
  eInvoice.fatturaElettronicaBody[0].datiPagamento = datiPagamentoFromFlows(flows);
  return eInvoice;
};

export const updateInvoiceCache = (invoice: DocumentWithAttachments, eInvoice?: ItalianEInvoice) => {
  // eslint-disable-next-line no-console
  console.debug('updating cache', invoice, eInvoice);

  queryClient.setQueryData(InvoicesQueryKeys.invoice(invoice.id), invoice);
  if (eInvoice) {
    queryClient.setQueryData(InvoicesQueryKeys.eInvoice(invoice.id), eInvoice);
  }
};

interface CreateDocumentParams {
  companyId: Company['id'];
  values: Partial<DocumentBuilderForm>;
}

function defaultVatCollection(counterpart: Counterpart | null): EsigibilitaIva | null {
  if (counterpart?.identityType === IdentityType.Pa) {
    return EsigibilitaIva.Split;
  }
  return null;
}

const resolveInvoiceFormat = (values: Partial<DocumentBuilderForm>) => {
  if (values.counterpart?.identityType === IdentityType.Pa) {
    return DocumentFormat.FPA;
  } else {
    return DocumentFormat.FPR;
  }
};

interface DocumentCreateRequest extends Omit<DocumentUpdateRequest, 'counterpart' | 'related'> {
  company: { id: string };
  counterpart?: Counterpart | null;
  grossAmount: { amount: string; currency: string };
  vatAmount: { amount: string; currency: string };
  vatAmountCompensation: { amount: string; currency: string };
  related?: { id: string | null }[];
}

const createDocument = async ({ companyId, values }: CreateDocumentParams) => {
  const currency = values.currency ?? 'EUR';

  const adjustedDocumentValues = adjustDocumentToUpsertByRules({ documentValues: values });

  const doc: DocumentCreateRequest = {
    category: values.categoryId ? { id: values.categoryId } : null,
    company: { id: companyId },
    creationDate: formatDateForAPI(values.date ?? new Date()),
    counterpart: values.counterpart,
    counterpartName: values.counterpart?.companyName ?? '',
    direction: DocumentDirection.issued,
    documentType: values.type ?? DocumentType.Invoice,
    eInvoiceType: values.subtype ?? defaultEInvoiceType(values.type ?? DocumentType.Invoice),
    format: resolveInvoiceFormat(values),
    isEInvoice: values.isEInvoice ?? true,
    grossAmount: { amount: '0', currency },
    notes: values.notes || null,
    subcategory: values.subcategoryId ? { id: values.subcategoryId } : null,
    vatAmount: { amount: '0', currency },
    vatCollection: esigibilitaToVatCollection(values.vatCollection ?? defaultVatCollection(values.counterpart ?? null)),
    vatAmountCompensation: { amount: '0', currency },
    related: values.relatedDocuments
      ?.filter((r) => r.dbDocumentId !== null)
      .map((r) => ({
        id: r.dbDocumentId,
      })),
    ...adjustedDocumentValues,
  };

  const payload = serialize(doc, DocumentResourceType, {
    relationships: ['company', 'counterpart', 'category', 'subcategory', 'related'],
  });

  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/documents', payload, { params: searchParams });

  return deserialize<DocumentWithAttachments>(data) as DocumentWithAttachments;
};

export const useCreateInvoiceFlow = () => {
  const { mutateAsync: createFlow } = useCreateFlow();
  return useMutation({
    mutationFn: ({
      invoiceId,
      currency,
      paymentInfo,
      flow,
    }: {
      invoiceId: string;
      currency: string;
      paymentInfo?: PaymentMethodForm | null;
      flow: DocumentBuilderFlowForm;
    }) =>
      createFlow({
        account: paymentInfo?.accountId ? { id: paymentInfo.accountId } : null,
        amount: { amount: flow.amount, currency },
        document: { id: invoiceId },
        expectedPaymentDate: flow.expectedPaymentDate,
        notes: '',
        paymentAccount: paymentInfo?.paymentAccount ?? '',
        paymentDate: flow.expectedPaymentDate,
        paymentMethod: paymentInfo?.paymentMethod ?? null,
        paymentStatus: flow.paymentStatus,
      }),
  });
};
export const useCreateInvoice = () => {
  const company = useActiveCompany();
  const { track } = useAnalytics();
  const { mutateAsync: createFlow } = useCreateInvoiceFlow();
  const invoicingSettings = useInvoicingSettings();

  return useMutation({
    mutationFn: async (values: Partial<DocumentBuilderForm>) => {
      const invoice = await createDocument({ companyId: company.id, values });
      const filledValues = fillValues(company.fiscalRegime, invoicingSettings, values);

      const createdFlows = await Promise.all(
        filledValues.flows.map((flow) =>
          createFlow({
            flow,
            invoiceId: invoice.id,
            paymentInfo: values.paymentInfo,
            currency: values.currency ?? 'EUR',
          }),
        ),
      );

      try {
        const eInvoice = await updateEInvoice({
          company,
          documentId: invoice.id,
          documentNumber: invoice.number,
          values: filledValues,
        });
        return { invoice: { ...invoice, flows: createdFlows }, eInvoice };
      } catch (error) {
        captureException(error, { extra: { values } });
        return { invoice: { ...invoice, flows: createdFlows } };
      }
    },
    onSuccess({ invoice, eInvoice }) {
      updateInvoiceCache(invoice, eInvoice);
      invalidateDocumentsAndFlowsCache();
      queryClient.invalidateQueries({
        queryKey: DocumentQueryKeys.allCompanyInfinityList(company.id),
        refetchType: 'all',
      });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.company(company.id) });
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        documentType: invoice.documentType,
        isElectronic: invoice.isEInvoice,
        context: 'document-builder',
      });
    },
  });
};

export const useCreateInvoiceForCounterpart = () => {
  const { track } = useAnalytics();
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  const { captureException } = useExceptionLogger();
  const createInvoice = useCreateInvoice();

  return useMutation({
    mutationFn: async (counterpart: Counterpart) => {
      return await createInvoice.mutateAsync({
        counterpart,
        isEInvoice: true,
        type: DocumentType.Invoice,
        subtype: EInvoiceType.Fattura,
        categoryId: counterpart.issuedCategory?.id,
        subcategoryId: counterpart.issuedSubcategory?.id,
        vatCollection: counterpart.identityType === IdentityType.Pa ? EsigibilitaIva.Split : null,
      });
    },
    onError(error, counterpart) {
      snack.error(t('alerts.error_creating_invoice'));
      captureException(error, { extra: { counterpartId: counterpart.id } });
    },
    onSuccess({ invoice }) {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        documentType: invoice.documentType,
        isElectronic: invoice.isEInvoice,
        context: 'document-builder',
      });
    },
  });
};

export const useCreateCreditNote = (document: DocumentWithAttachments) => {
  const { track } = useAnalytics();
  const duplicateDocument = useDuplicateDocument();
  const invoicingSettings = useInvoicingSettings();
  const activeCompany = useActiveCompany();
  const [useGrossPrices] = useGrossPricesState();

  return useMutation({
    mutationFn: async () => {
      const eInvoiceForValues = await getFullEInvoice(document.id);
      const values = eInvoiceToForm(
        invoicingSettings,
        activeCompany.fiscalRegime,
        document,
        eInvoiceForValues,
        document.documentType,
        useGrossPrices,
      );
      const items = values.items;

      const newCreditNote = await duplicateDocument.mutateAsync({
        counterpart: document.counterpart,
        categoryId: document.category?.id,
        flows: [
          {
            remoteId: null,
            amount: document.grossAmount.amount,
            notes: '',
            expectedPaymentDate: new Date(),
            paymentStatus: FlowPaymentStatus.ToPay,
          },
        ],
        items,
        type: DocumentType.CreditNote,
        subcategoryId: document.subcategory?.id,
        subtype: EInvoiceType.NotaDiCredito,
        isEInvoice: document.isEInvoice,
        relatedDocuments: document.number
          ? [
              {
                dbDocumentId: document.id,
                type: RelatedDocumentType.FatturaCollegata,
                documentNumber: document.number,
                date: parseISO(document.creationDate),
                CIGCode: '',
                CUPCode: '',
                orderCode: '',
              },
            ]
          : [],
        socialSecurity: values.socialSecurity,
        eInvoice: prepareCreateCreditNoteData(document, eInvoiceForValues),
      });
      return { creditNote: newCreditNote.document };
    },
    onSuccess({ creditNote }) {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        documentType: creditNote.documentType,
        isElectronic: creditNote.isEInvoice,
        context: 'document-builder',
      });
    },
    onError: (err) => {
      captureException(err, {
        extra: {
          document: document.id,
          help_text: 'credit note creation',
        },
      });
    },
  });
};

export const useCreateDeliveryNoteFromAside = (document: DocumentWithAttachments) => {
  const { track } = useAnalytics();
  const duplicateDocument = useDuplicateDocument();
  const invoicingSettings = useInvoicingSettings();
  const activeCompany = useActiveCompany();
  const [useGrossPrices] = useGrossPricesState();

  return useMutation({
    mutationFn: async () => {
      const eInvoiceForValues = await getFullEInvoice(document.id);
      const values = eInvoiceToForm(
        invoicingSettings,
        activeCompany.fiscalRegime,
        document,
        eInvoiceForValues,
        document.documentType,
        useGrossPrices,
      );
      const items = values.items;

      const relatedDocumentType = documentTypeToRelatedDocument(document.documentType);

      const counterpart = document.counterpart;
      const deliveryDestination = {
        address: counterpart?.deliveryAddress ?? null,
        postalCode: counterpart?.deliveryPostalCode ?? null,
        city: counterpart?.deliveryCity ?? null,
        country: counterpart?.deliveryCountry ?? null,
      };

      const companyDestination = {
        address: counterpart?.address ?? null,
        postalCode: counterpart?.postalCode ?? null,
        city: counterpart?.city ?? null,
        country: counterpart?.country ?? null,
      };
      const destination = Object.values(deliveryDestination).some((x) => x) ? deliveryDestination : companyDestination;

      const newDeliveryNote = await duplicateDocument.mutateAsync({
        counterpart: document.counterpart,
        categoryId: document.category?.id,
        flows: [],
        items,
        type: DocumentType.DeliveryNote,
        subcategoryId: document.subcategory?.id,
        subtype: null,
        isEInvoice: document.isEInvoice,
        transportInformations: {
          destination: {
            address: destination.address ?? '',
            postalCode: destination.postalCode ?? '',
            city: destination.city ?? '',
            country: destination.country ?? 'IT',
          },
          transportReason: null,
          courier: null,
          packsNumber: null,
          weight: null,
        },
        relatedDocuments: document.number
          ? [
              {
                dbDocumentId: document.id,
                type: relatedDocumentType,
                documentNumber: document.number,
                date: parseISO(document.creationDate),
                CIGCode: '',
                CUPCode: '',
                orderCode: '',
              },
            ]
          : [],
        eInvoice: prepareCreateDeliveryNoteFromAsideData(document, eInvoiceForValues, destination),
      });
      return { deliveryNote: newDeliveryNote.document };
    },
    onSuccess({ deliveryNote }) {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        documentType: deliveryNote.documentType,
        isElectronic: deliveryNote.isEInvoice,
        context: 'document-builder',
      });
    },
    onError: (err) => {
      captureException(err, {
        extra: {
          document: document.id,
          help_text: 'delivery note creation from aside',
        },
      });
    },
  });
};

export const useCreateInvoiceFromAside = () => {
  const { track } = useAnalytics();
  const duplicateDocument = useDuplicateDocument();
  const invoicingSettings = useInvoicingSettings();
  const activeCompany = useActiveCompany();
  const [useGrossPrices] = useGrossPricesState();

  return useMutation({
    mutationFn: async (document: DocumentWithAttachments) => {
      const eInvoice = await getFullEInvoice(document.id);

      const values = eInvoiceToForm(
        invoicingSettings,
        activeCompany.fiscalRegime,
        document,
        eInvoice,
        document.documentType,
        useGrossPrices,
      );
      assertIsDefined(eInvoice.fatturaElettronicaBody[0], 'body must be defined');
      const motivation = eInvoice.fatturaElettronicaBody[0].datiGenerali.datiGeneraliDocumento.causale?.join('') ?? '';
      const items = values.items;
      const socialSecurity = values.socialSecurity;
      const stamp = values.stamp;
      const paymentInfo = values.paymentInfo;
      const transportInformations = values.transportInformations;
      const withholdingTax = values.withholdingTax;

      const relatedDocumentType = documentTypeToRelatedDocument(document.documentType);
      const newInvoice = await duplicateDocument.mutateAsync({
        counterpart: document.counterpart,
        categoryId: document.category?.id,
        flows:
          document.flows.length > 0
            ? document.flows.map((flow) => ({
                ...parseDocumentFlow(flow),
                paymentStatus: FlowPaymentStatus.ToPay,
              }))
            : [
                {
                  remoteId: null,
                  amount: document.grossAmount.amount,
                  notes: '',
                  expectedPaymentDate: new Date(),
                  paymentStatus: FlowPaymentStatus.ToPay,
                },
              ],
        items,
        motivation,
        type: DocumentType.Invoice,
        subcategoryId: document.subcategory?.id,
        subtype: EInvoiceType.Fattura,
        isEInvoice: document.documentType === DocumentType.Quote || document.isEInvoice,
        relatedDocuments: document.number
          ? [
              {
                dbDocumentId: document.id,
                type: relatedDocumentType,
                documentNumber: document.number,
                date: parseISO(document.creationDate),
                CIGCode: '',
                CUPCode: '',
                orderCode: '',
              },
            ]
          : [],
        socialSecurity,
        stamp,
        paymentInfo,
        transportInformations,
        withholdingTax,
        eInvoice: prepareCreateInvoiceFromAsideData(document, eInvoice),
      });
      return { invoice: newInvoice.document };
    },
    onSuccess({ invoice }) {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        documentType: invoice.documentType,
        isElectronic: invoice.isEInvoice,
        context: 'document-builder',
      });
    },
    onError: (err, args) => {
      captureException(err, {
        extra: {
          document: args.id,
          help_text: 'invoice creation from aside',
        },
      });
    },
  });
};

export const useCreateInvoiceFromDeliveryNotes = () => {
  const { track } = useAnalytics();
  const createInvoice = useCreateInvoice();
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: async (deliveryNotes: Document[]) => {
      const firstDeliveryNote = deliveryNotes.at(0);
      assertIsDefined(firstDeliveryNote, 'At least a delivery note must exists');

      const items: DocumentDataLine[] = [];
      const relatedDocs: RelatedDocumentLine[] = [];

      for (const deliveryNote of deliveryNotes) {
        relatedDocs.push({
          dbDocumentId: deliveryNote.id,
          type: RelatedDocumentType.Ddt,
          documentNumber: deliveryNote.number ?? '',
          date: parseISO(deliveryNote.creationDate),
          CIGCode: '',
          CUPCode: '',
          orderCode: '',
        });

        const einvoice = await getFullEInvoice(deliveryNote.id);
        einvoice.fatturaElettronicaBody.at(0)?.datiBeniServizi.dettaglioLinee.forEach((dettaglioLinea) => {
          const item = parseEInvoiceItem(dettaglioLinea);
          const existingItem = items.find(
            (el) =>
              el.code === item.code &&
              el.description === item.description &&
              el.unit === item.unit &&
              el.netPrice === item.netPrice &&
              el.vat.lawReference === item.vat.lawReference &&
              el.vat.percentage === item.vat.percentage &&
              el.vat.taxExemption === item.vat.taxExemption &&
              el.riferimentoAmministrazione === item.riferimentoAmministrazione &&
              el.discounts[0] === item.discounts[0], // for the moment the builder manages only one discount even if electronic invoicing allows more discounts
          );

          if (existingItem) {
            existingItem.quantity = BigNumber(existingItem.quantity).plus(item.quantity).toFixed();
          } else {
            items.push(item);
          }
        });
      }

      const newInvoice = await createInvoice.mutateAsync({
        counterpart: firstDeliveryNote.counterpart,
        categoryId: firstDeliveryNote.category?.id,
        flows: [
          {
            remoteId: null,
            amount: '0',
            notes: '',
            expectedPaymentDate: new Date(),
            paymentStatus: FlowPaymentStatus.ToPay,
          },
        ],
        items: items.map(({ discounts, ...rest }) => ({ ...rest, discount: discounts[0] ?? defaultDiscount })), // for the moment the builder manages only one discount even if electronic invoicing allows more discounts
        motivation: '',
        type: DocumentType.Invoice,
        subcategoryId: firstDeliveryNote.subcategory?.id,
        subtype: EInvoiceType.Fattura,
        isEInvoice: true,
        relatedDocuments: relatedDocs,
      });
      return newInvoice;
    },
    onSuccess({ invoice }) {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        documentType: invoice.documentType,
        isElectronic: invoice.isEInvoice,
        context: 'multiple-delivery-notes-aside',
      });
    },
    onError: (err, args) => {
      captureException(err, {
        extra: {
          delivery_notes: args.map((el) => el.id).join(','),
          help_text: 'invoice creation from delivery notes',
        },
      });
    },
  });
};

export const useDuplicateInvoice = () => {
  const { track } = useAnalytics();
  const duplicateDocument = useDuplicateDocument();
  const invoicingSettings = useInvoicingSettings();
  const activeCompany = useActiveCompany();
  const [useGrossPrices] = useGrossPricesState();

  return useMutation({
    mutationFn: async (invoice: Document) => {
      let motivation = '';
      let items: DocumentBuilderLine[] = [];
      let socialSecurity: DocumentBuilderForm['socialSecurity'] = null;
      let stamp: DocumentBuilderForm['stamp'] = null;
      let relatedDocuments: RelatedDocumentLine[] = [];
      let paymentInfo: DocumentBuilderForm['paymentInfo'] | undefined = undefined;
      let transportInformations: DocumentBuilderForm['transportInformations'] = null;
      let withholdingTax: DocumentBuilderForm['withholdingTax'] = [];
      let subType: EInvoiceType | null = null;
      let eInvoice: ItalianEInvoice | undefined = undefined;

      try {
        eInvoice = await getFullEInvoice(invoice.id);
        const values = eInvoiceToForm(
          invoicingSettings,
          activeCompany.fiscalRegime,
          invoice,
          eInvoice,
          invoice.documentType,
          useGrossPrices,
        );
        assertIsDefined(eInvoice.fatturaElettronicaBody[0], 'body must be defined');
        motivation = eInvoice.fatturaElettronicaBody[0].datiGenerali.datiGeneraliDocumento.causale?.join('') ?? '';
        items = values.items;
        socialSecurity = values.socialSecurity;
        stamp = values.stamp;
        relatedDocuments = values.relatedDocuments;
        paymentInfo = values.paymentInfo;
        transportInformations = values.transportInformations;
        withholdingTax = values.withholdingTax;
        subType = values.subtype;
      } catch (error) {
        captureException(error);
      }

      const duplicatedInvoice = await duplicateDocument.mutateAsync({
        categoryId: invoice.category?.id,
        counterpart: invoice.counterpart,
        date: new Date(),
        flows: invoice.flows?.map((flow) => ({
          ...parseDocumentFlow(flow),
          expectedPaymentDate: calculatePresetDate(new Date(), invoice.counterpart?.paymentDate),
          paymentStatus: FlowPaymentStatus.ToPay,
        })),
        items,
        motivation,
        notes: invoice.notes ?? undefined,
        isEInvoice: invoice.isEInvoice,
        subcategoryId: invoice.subcategory?.id,
        subtype: subType ?? defaultEInvoiceType(invoice.documentType),
        type: invoice.documentType,
        vatCollection:
          parseDocumentVatCollection(invoice.vatCollection) ?? defaultVatCollection(invoice.counterpart ?? null),
        relatedDocuments,
        socialSecurity,
        stamp,
        paymentInfo,
        transportInformations,
        withholdingTax,
        eInvoice,
      });

      return { invoice: duplicatedInvoice.document };
    },
    onSuccess({ invoice }) {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        documentType: invoice.documentType,
        isElectronic: invoice.isEInvoice,
        context: 'document-builder',
      });
    },
  });
};

interface UpdateInvoiceParams {
  invoiceId: DocumentWithAttachments['id'];
  values: DocumentBuilderForm;
}

const updateInvoice = async ({ invoiceId, values }: UpdateInvoiceParams) => {
  const adjustedDocumentValues = adjustDocumentToUpsertByRules({ documentValues: values });

  const doc: DocumentUpdateRequest = {
    category: values.categoryId ? { id: values.categoryId } : null,
    creationDate: formatDateForAPI(values.date),
    counterpart: values.counterpart ?? null,
    counterpartName: values.counterpart?.companyName,
    direction: values.type === DocumentType.SelfInvoice ? DocumentDirection.received : DocumentDirection.issued,
    documentType: values.type,
    eInvoiceType: values.subtype ?? defaultEInvoiceType(values.type),
    format: resolveInvoiceFormat(values),
    isEInvoice: values.isEInvoice,
    notes: values.notes || null,
    subcategory: values.subcategoryId ? { id: values.subcategoryId } : null,
    vatCollection: esigibilitaToVatCollection(values.vatCollection || null),
    related: values.relatedDocuments
      .filter((r) => r.dbDocumentId !== null)
      .map((r) => ({
        id: r.dbDocumentId,
      })),
    ...adjustedDocumentValues,
  };

  const payload = serialize(doc, DocumentResourceType, {
    relationships: ['company', 'counterpart', 'category', 'subcategory', 'related'],
  });

  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/documents/${invoiceId}`, payload, {
    params: searchParams,
  });

  return deserialize<DocumentWithAttachments>(data) as DocumentWithAttachments;
};

const getFullEInvoice = async (documentId: Document['id']): Promise<ItalianEInvoice> => {
  const data = await getEInvoice(documentId);

  return await decorateEInvoiceWithFlows(documentId, data);
};

const getEBill = async (documentId: Document['id']): Promise<ItalianEBill | null> => {
  try {
    const { data } = await backendApiGateway.get<{ data: ItalianEBill }>(`/api/v1/documents/${documentId}/einvoice`);

    return data.data;
  } catch (error) {
    console.error('Error fetching eBill', error);
    return null;
  }
};

export const useEInvoice = (
  documentId: Document['id'] | undefined,
  options: {
    enabled: boolean;
  } = { enabled: true },
) =>
  useQuery({
    enabled: options.enabled && Boolean(documentId),
    queryFn: async () => {
      assertIsDefined(documentId, 'documentid must be defined');
      return await getFullEInvoice(documentId);
    },
    queryKey: InvoicesQueryKeys.eInvoice(documentId ?? ''),
    refetchOnMount: false,
    staleTime: 60000,
  });

export const useEBill = (
  billId: Document['id'] | undefined,
  options: {
    enabled: boolean;
  } = { enabled: true },
) =>
  useQuery({
    queryFn: async () => {
      assertIsDefined(billId, 'documentid must be defined');
      return await getEBill(billId);
    },
    queryKey: InvoicesQueryKeys.eBill(billId ?? ''),
    staleTime: 60000,
    enabled: options.enabled && Boolean(billId),
    refetchOnMount: false,
  });

export const useEBills = (
  billId: Document['id'][],
  options: {
    enabled: boolean;
  } = { enabled: true },
) =>
  useQueries({
    queries: billId.map((id) => ({
      queryFn: async () => {
        return await getEBill(id);
      },
      queryKey: InvoicesQueryKeys.eBill(id),
      staleTime: 60000,
      enabled: options.enabled && Boolean(id),
      refetchOnMount: false,
    })),
  });

interface UpdateEInvoiceParams {
  company: Company;
  documentId: Document['id'];
  documentNumber: Document['number'];
  values: DocumentBuilderForm;
}

const updateEInvoice = async ({
  company,
  documentId,
  documentNumber,
  values,
}: UpdateEInvoiceParams): Promise<ItalianEInvoice> => {
  const einvoice = { data: valuesToEInvoice(company, values, documentNumber) };

  const { data } = await backendApiGateway.post<{ data: ItalianEInvoice }>(
    `/api/v1/documents/${documentId}/einvoice`,
    einvoice,
  );

  return await decorateEInvoiceWithFlows(documentId, data.data);
};

interface FinalizeInvoiceParams {
  invoiceId: string;
  number: string;
  progressive?: string;
  sectionalId?: string | null;
}

const finalizeInvoice = async ({
  invoiceId,
  number,
  progressive,
  sectionalId,
}: FinalizeInvoiceParams): Promise<void> => {
  await backendApiGateway.post(
    `/api/v1/documents/${invoiceId}/issue`,
    { number, progressive, sectionalId },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

export const useSuggestedInvoiceNumber = ({
  invoiceType,
  invoiceDirection,
  invoiceCreationDate,
  invoiceNumber,
}: {
  invoiceType?: DocumentType;
  invoiceDirection?: DocumentDirection;
  invoiceCreationDate?: string;
  invoiceNumber: string | null;
}): { lastInvoiceNumbers: string[]; nextInvoiceNumber: string | null } => {
  const lastDocumentNumbers = useLastDocumentsNumberQuery(invoiceType, invoiceDirection, invoiceCreationDate);
  const lastInvoiceNumbers = highestInvoiceNumbersByPattern(lastDocumentNumbers.data ?? []);
  const lastInvoiceNumber = getHighestInvoiceNumber(lastDocumentNumbers.data ?? []);
  const nextInvoiceNumber = invoiceNumber ?? increaseInvoiceNumber(lastInvoiceNumber);
  return { lastInvoiceNumbers, nextInvoiceNumber };
};

export const useSuggestedInvoiceNumberWithSectional = ({
  invoiceType,
  invoiceDirection,
  invoiceCreationDate,
}: {
  invoiceType?: DocumentType;
  invoiceDirection?: DocumentDirection;
  invoiceCreationDate?: string;
}): DocumentNumberWithSectional[] => {
  const lastDocumentNumbers = useLastDocumentsNumberQuery(invoiceType, invoiceDirection, invoiceCreationDate);
  const lastInvoiceNumbers = useMemo(
    () => highestInvoiceNumbersBySectional(lastDocumentNumbers.data ?? []),
    [lastDocumentNumbers.data],
  );

  return lastInvoiceNumbers;
};

const useSaveInvoiceFlow = () => {
  const updateFlow = useUpdateFlow('/documents/builder');
  return useMutation({
    mutationFn: async ({
      flow,
      currency,
      paymentInfo,
      flowDirection,
    }: {
      currency: string;
      flow: DocumentBuilderFlowForm;
      paymentInfo: PaymentMethodForm | null;
      flowDirection: CashflowDataDirection;
    }) => {
      assertIsDefined(flow.remoteId, 'remoteId must be defiend.');

      const result = await updateFlow.mutateAsync({
        id: flow.remoteId,
        payloadData: {
          amount: flow.amount ? { amount: flow.amount, currency } : undefined,
          expectedPaymentDate: flow.expectedPaymentDate,
          paymentDate: flow.expectedPaymentDate,
          paymentStatus: flow.paymentStatus,
          paymentAccount: paymentInfo?.paymentAccount,
          paymentMethod: paymentInfo?.paymentMethod,
          account: paymentInfo?.accountId ? { id: paymentInfo.accountId } : null,
        },
        updatedAttributes: [
          'amount',
          'expectedPaymentDate',
          'paymentStatus',
          'paymentAccount',
          'paymentMethod',
          'account',
        ],
        flowDirection,
      });

      return result;
    },
  });
};

interface SaveInvoiceParams {
  invoiceId?: DocumentWithAttachments['id'];
  values: DocumentBuilderForm;
}

export const useSaveInvoice = () => {
  const createInvoice = useCreateInvoice();
  const company = useActiveCompany();
  const { mutateAsync: createFlow } = useCreateInvoiceFlow();
  const saveFlow = useSaveInvoiceFlow();
  const mapResponseErrors = useMapResponseErrors();
  const updateCounterpart = useUpdateCounterpart();

  return useMutation({
    mutationKey: InvoicesMutationKeys.saveInvoice,
    mutationFn: async ({ invoiceId, values }: SaveInvoiceParams) => {
      const { counterpart } = values;

      // Update counterpart destination code if empty
      if (counterpart && _isEmpty(counterpart.destinationCode)) {
        const newDestinationCode = defaultDestinationCode(
          counterpart.destinationCode,
          counterpart.country,
          counterpart.identityType ?? IdentityType.Company,
        );

        await updateCounterpart.mutateAsync({
          counterpart,
          data: {
            destinationCode: newDestinationCode,
          },
        });
      }

      if (invoiceId) {
        const updatedInvoice = await updateInvoice({ invoiceId, values });
        updateInvoiceCache(updatedInvoice);
        const flows = await Promise.all(
          values.flows.map((flow) => {
            if (flow.remoteId) {
              return saveFlow.mutateAsync({
                flow,
                paymentInfo: values.paymentInfo,
                currency: values.currency,
                flowDirection: updatedInvoice.isInflow ? 'inflow' : 'outflow',
              });
            }
            return createFlow({
              flow,
              invoiceId: updatedInvoice.id,
              paymentInfo: values.paymentInfo,
              currency: values.currency,
            });
          }),
        );
        const eInvoice = await updateEInvoice({
          company,
          documentId: updatedInvoice.id,
          documentNumber: updatedInvoice.number,
          values: values.type === DocumentType.DeliveryNote ? values : { ...values, transportInformations: null },
        });
        return { invoice: { ...updatedInvoice, flows }, eInvoice };
      } else {
        return await createInvoice.mutateAsync(
          values.type === DocumentType.DeliveryNote ? values : { ...values, transportInformations: null },
        );
      }
    },
    onError: mapResponseErrors,
    onSuccess: ({ invoice, eInvoice }) => updateInvoiceCache(invoice, eInvoice),
    scope: { id: 'saveInvoice' },
  });
};

interface UseFinalizeInvoiceParams {
  invoice: DocumentWithAttachments;
  einvoice: DocumentBuilderForm;
  number: string;
  progressive?: bigint;
  sectionalId?: string | null;
}

export const useFinalizeInvoice = () => {
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: async ({ invoice, number, progressive, sectionalId }: UseFinalizeInvoiceParams) => {
      return await finalizeInvoice({
        invoiceId: invoice.id,
        progressive: progressive ? String(progressive) : undefined,
        number,
        sectionalId,
      });
    },
    onSuccess: async (_, { einvoice, invoice }) => {
      await queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.invoice(invoice.id) });
      await queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.eInvoice(invoice.id) });
      await queryClient.invalidateQueries({ queryKey: InvoicesQueryKeys.countByStatusAll });

      track(AnalyticsEvents.DOCUMENT_ISSUED, eInvoiceAnalytics(invoice, einvoice));
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

const fetchInvoice = async (id: Document['id']) => {
  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/documents/${id}`, { params: searchParams });

  return deserialize<DocumentWithAttachments>(data) as DocumentWithAttachments;
};

export const useInvoice = (invoiceId?: Document['id']) => {
  const activeCompanyId = useActiveCompanyId();

  return useQuery({
    queryKey: InvoicesQueryKeys.invoice(invoiceId ?? ''),
    queryFn: async () => {
      const invoice = await fetchInvoice(invoiceId || '');

      // Check if the invoice belongs to the active company
      // This is a security measure to prevent unauthorized access to invoices from other companies.
      if (invoice.company.id != activeCompanyId) {
        throw new Error('Unauthorized');
      }

      return invoice;
    },
    enabled: Boolean(invoiceId),
    staleTime: 60000,
    refetchOnMount: false,
  });
};

const fetchSharedInvoice = async (token: string) => {
  const shareIncludeBuilder = jsonAPIIncludeBuilder([
    'attachments',
    'company',
    'company.companyIdentity',
    'counterpart',
    'flows',
  ]);
  const params = buildJsonAPIQueryParams(shareIncludeBuilder);
  const { data } = await backendApiGateway.get<SharedEInvoice>(`/api/share-invoice/${token}`, { params });
  return data.data;
};

export const useSharedInvoice = (token: string) => {
  return useQuery({
    queryFn: async () => await fetchSharedInvoice(token),
    queryKey: InvoicesQueryKeys.sharedInvoice(token),
  });
};

const sendInvoicePreview = async ({
  to,
  subject,
  body,
  from,
  invoice,
}: {
  to: string[];
  subject: string;
  body: string;
  from: string;
  invoice: DocumentWithAttachments;
}) => {
  await backendApiGateway.post<DocumentObject>(`api/v1/documents/${invoice.id}/share-invoice`, {
    to,
    subject,
    body,
    from,
  });
};

export const useSendInvoicePreview = () => {
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: sendInvoicePreview,
    onSuccess: (_, { invoice }) => {
      track(
        invoice.status === DocumentStatus.Draft
          ? AnalyticsEvents.DOCUMENT_DRAFT_SUBMITTED
          : AnalyticsEvents.DOCUMENT_COPY_SUBMITTED,
        {
          context: 'document-builder',
          documentType: invoice.documentType,
          counterpartContactEmail: invoice.counterpart?.contactEmail,
          counterpartCertifiedEmail: invoice.counterpart?.certifiedEmail,
        },
      );
    },
  });
};

const fetchStatusNumber = async (
  companyId: Company['id'],
  statuses: DocumentStatus[],
  documentType: DocumentType[],
): Promise<number> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  filterBuilder.in('document_type', documentType);
  filterBuilder.in('status', statuses);

  const searchParams = buildJsonAPIQueryParams(filterBuilder);

  const {
    data: { total },
  } = await backendApiGateway.get<{ total: number }>('/api/v1/documents/metadata', {
    params: searchParams,
  });
  return total;
};

export const useInvoicesCountByStatus = (statuses: DocumentStatus[], documentType: DocumentType[]) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: InvoicesQueryKeys.countByStatus(statuses, documentType),
    queryFn: () => fetchStatusNumber(companyId, statuses, documentType),
    enabled: !!companyId,
  });
};

export const useDeleteInvoiceFlow = useDeleteFlow;

export const useUploadInvoicesXml = () => {
  const companyId = useActiveCompanyId();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: async (data: {
      file: File;
      onProgress: (progress: number) => void;
      issue: boolean;
      recalculateFileName: boolean;
      truncateAttachmentFileName: boolean;
    }) => {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        context: 'xml-import',
        isElectronic: true,
        filename: data.file.name,
        issue: data.issue,
      });

      const extraFilterBuilder = jsonAPIExtraQueryBuilder();
      extraFilterBuilder.add('company_id', companyId);
      extraFilterBuilder.add('issue', data.issue);
      extraFilterBuilder.add('filename', data.file.name);
      extraFilterBuilder.add('recalculate_filename', data.recalculateFileName);
      extraFilterBuilder.add('truncate_attachment_filename', data.truncateAttachmentFileName);

      const searchParams = buildJsonAPIQueryParams(extraFilterBuilder);

      const fileData = await data.file.arrayBuffer();

      return backendApiGateway.post<{ document_ids: string[] }>('/api/v1/documents/upload-invoice', fileData, {
        params: searchParams,
        onUploadProgress: (event) => data.onProgress(event.progress ?? 0),
        headers: {
          'Content-Type': data.file.name.endsWith('p7m') ? 'application/p7m' : 'application/xml',
        },
      });
    },
    onSuccess: ({ data: response }, data) => {
      if (data.issue) {
        response.document_ids.forEach((documentId) => {
          track(AnalyticsEvents.DOCUMENT_ISSUED, {
            documentId,
            context: 'xml-import',
            isElectronic: true,
            filename: data.file.name,
          });
        });
      }
    },
  });
};

export const useImportExistingInvoices = () => {
  const companyId = useActiveCompanyId();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: async (data: { file: File; onProgress: (progress: number) => void; direction: DocumentDirection }) => {
      track(AnalyticsEvents.DOCUMENT_CREATION_STARTED, {
        context: 'xml-import',
        isElectronic: true,
        filename: data.file.name,
      });

      const extraFilterBuilder = jsonAPIExtraQueryBuilder();
      extraFilterBuilder.add('company_id', companyId);
      extraFilterBuilder.add('direction', data.direction);
      extraFilterBuilder.add('filename', data.file.name);

      const searchParams = buildJsonAPIQueryParams(extraFilterBuilder);

      const fileData = await data.file.arrayBuffer();

      return backendApiGateway.post('/api/v1/documents/import', fileData, {
        params: searchParams,
        onUploadProgress: (event) => data.onProgress(event.progress ?? 0),
        headers: {
          'Content-Type': data.file.name.endsWith('p7m') ? 'application/p7m' : 'application/xml',
        },
      });
    },
  });
};

export const useExportInvoices = () => {
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: async ({
      from,
      to,
      requested,
      email,
      decode,
    }: {
      from: string;
      to: string;
      requested: string[];
      email: string;
      decode: boolean;
    }) => {
      const payload = { companyId, from, to, requested, email, decode };
      return backendApiGateway.post('/api/v1/documents/export', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
    },
  });
};

interface DuplicateEInvoiceParams {
  documentId: Document['id'];
  eInvoice: ItalianEInvoice;
}
const duplicateEInvoice = async ({ documentId, eInvoice }: DuplicateEInvoiceParams): Promise<ItalianEInvoice> => {
  const { data } = await backendApiGateway.post<{ data: ItalianEInvoice }>(`/api/v1/documents/${documentId}/einvoice`, {
    data: eInvoice,
  });

  return await decorateEInvoiceWithFlows(documentId, data.data);
};

const useDuplicateDocument = () => {
  const company = useActiveCompany();
  const { mutateAsync: createFlow } = useCreateInvoiceFlow();
  const invoicingSettings = useInvoicingSettings();

  return useMutation({
    mutationFn: async (values: Partial<DocumentBuilderForm> & { eInvoice?: ItalianEInvoice }) => {
      const newDocument = await createDocument({ companyId: company.id, values });
      const filledValues = fillValues(company.fiscalRegime, invoicingSettings, values);

      const createdFlows = await Promise.all(
        filledValues.flows.map((flow) =>
          createFlow({
            flow,
            invoiceId: newDocument.id,
            paymentInfo: values.paymentInfo,
            currency: values.currency ?? 'EUR',
          }),
        ),
      );

      if (!values.eInvoice) {
        return { document: { ...newDocument, flows: createdFlows } };
      }

      try {
        const eInvoice = await duplicateEInvoice({
          documentId: newDocument.id,
          eInvoice: {
            ...values.eInvoice,
            fatturaElettronicaBody: values.eInvoice.fatturaElettronicaBody.map((body) => ({
              ...body,
              datiGenerali: {
                ...body.datiGenerali,
                datiGeneraliDocumento: {
                  ...body.datiGenerali.datiGeneraliDocumento,
                  data: formatDateForAPI(new Date()),
                  numero: null,
                },
              },
            })),
          },
        });
        return { document: { ...newDocument, flows: createdFlows }, eInvoice };
      } catch (error) {
        captureException(error, { extra: { values } });
        return { document: { ...newDocument, flows: createdFlows } };
      }
    },
    onSuccess({ document, eInvoice }) {
      updateInvoiceCache(document, eInvoice);
    },
  });
};

export const useImportInvoices = () => {
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (payload: InvoiceImportPayload) => importInvoices({ companyId, payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
    },
  });
};
